import moment from 'moment';
import { useRef, useState, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { RadioButton } from 'primereact/radiobutton';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputMask } from 'primereact/inputmask';
import {
  applyFieldRules,
  getSectionHeaders,
  provinces,
} from '../../appconfig/Settings';
import { postAllData, getTwoFilteredDetails } from '../../service/ApiService';
import {
  CONNECT_WITH_US_FORM,
  GET_EMPLOYEE_FORM_BY_SIN_EMAIL,
} from '../../config/ApiConstant';
import { validateCurrentSection } from './userFormValidation';

// Import components
import PersonalSection from './FormComponents/PersonalSection';
import EmploymentSection from './FormComponents/EmploymentSection';
import DocumentsSection from './FormComponents/DocumentsSection';
import SpouseDocumentsSection from './FormComponents/SpouseDocumentsSection';
import FormActions from './FormComponents/FormActions';
import FormHeader from './FormComponents/FormHeader';
import TaxSummary from './FormComponents/TaxSummary';
import GeneralInformationSection from './FormComponents/GeneralInformation';

const UserFormNew = () => {
  document.title = 'Nagra Accounting | Form';

  // initail form data
  const initialUserData = {
    landlords: [{ name: '', amount: 0, months: '' }],
    generalOtherExpenses: [{ amount: '', name: '' }],
    dependentChildren: [{ child_name: '', child_dob: null }],
    employedOtherExpenses: [{ amount: '', name: '' }],
    spouseEmployedOtherExpenses: [{ amount: '', name: '' }],
    wfhEmployedOtherExpenses: [{ amount: '', name: '' }],
    spouseWfhEmployedOtherExpenses: [{ amount: '', name: '' }],
    long_haul_truck_driver: '0',
    filed_with_nagra_before: '',
    returningSIN: '',
    returningEmail: '',
  };
  // const initialUserData = {};

  // get form user data from local storage
  const [userData, setUserData] = useState<any>(() => {
    const saved = localStorage.getItem('userFormData');
    let parsedData = saved ? JSON.parse(saved) : initialUserData;

    // Default rental_income to '0' if it's not set
    if (!parsedData.rental_income) {
      parsedData = { ...parsedData };
    }

    // Remove all file fields from localStorage data**
    const fileFields = [
      't4_file',
      't4a_file',
      't5_file',
      't5007_file',
      't5008_file',
      't2202a_file',
      'uber_file',
      'other_file',
      'spouse_t4_file',
      'spouse_t4a_file',
      'spouse_t5_file',
      'spouse_t5007_file',
      'spouse_t5008_file',
      'spouse_t2202a_file',
      'spouse_uber_file',
      'spouse_other_file',
    ];

    fileFields.forEach((field) => {
      delete parsedData[field];
    });

    return {
      ...initialUserData,
      ...parsedData,
    };
  });

  // error obj
  const [errors, setErrors] = useState<any>({});
  const [activeSection, setActiveSection] = useState('identification');
  const [searchErrors, setSearchErrors] = useState<any>({});

  // loading
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  //pdf loader
  const [downloadPDFLoading, setDownloadPDFLoading] = useState<boolean>(false);

  // toast ref
  const toast = useRef<any>(null);

  // Calculate the minimum date for dob
  const today = new Date();
  const maxDate = new Date();
  maxDate.setFullYear(today.getFullYear() - 14);

  const [isSearchingClient, setIsSearchingClient] = useState(false);

  // Spouse header section based on marital status
  const isMarried =
    (userData?.dec_marrige_status === 'Married' ||
      userData?.dec_marrige_status === 'Living common-law') &&
      userData.is_spouse_canadian_resident === '1'
      ? true
      : false;

  // dynamically add section if married
  const sectionHeaders = getSectionHeaders(isMarried);

  // const today date
  const todayAsMaxDate = moment().subtract(1, 'days').toDate();

  // display disclaimer of basic tax
  const [basicTaxSummary, setBasicTaxSummary] = useState<any>();
  const [taxPayerId, setTaxPayerId] = useState<any>(null);

  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const updateFormState = () => {
    setFormSubmitted(true);
  };

  // tax payer summary previous button
  const updatePreviousformState = () => {
    setFormSubmitted(false);
  };

  // payment url state

  const [paymentPageUrl, setPaymentPageUrl] = useState<any>(null);

  // on input handle change
  const onInputChange = (e: any, name: string) => {
    let val;
    if (name === 'has_agreed') {
      val = e.checked ? true : false;
    } else {
      val = e?.target?.value ?? e?.value;
    }

    // FILE INPUT
    if (e?.files) {
      val = Array.isArray(e.files) ? e.files : e.files;
    }

    // DATE FORMAT
    const dateFields = [
      'date_of_entry',
      'date_of_birth',
      'moving_date',
      'spouse_moving_date',
      'spouse_date_of_entry',
      'spouse_dob',
      'marrige_status_changed_date',
      'child_dob',
    ];

    // format date fields
    if (dateFields.includes(name) && val) {
      val = moment(val).format('YYYY-MM-DD');
    }

    // POSTAL CODE
    if ((name === 'postal_code' || name === 'spouse_postal_code') && val) {
      val = val.replace(/\s+/g, '').toUpperCase();
      if (val.length > 3) {
        val = `${val.slice(0, 3)} ${val.slice(3, 6)}`;
      }
    }

    let updatedData = { ...userData, [name]: val };
    let updatedErrors = { ...errors, [name]: '' };

    // Apply switch logic externally (for radio button clear field and error)
    applyFieldRules(name, val, updatedData, updatedErrors);

    setUserData(updatedData);
    setErrors(updatedErrors);

    // File fields that should NOT be stored in localStorage
    const fileFields = [
      't4_file',
      't4a_file',
      't5_file',
      't5007_file',
      't5008_file',
      't2202a_file',
      'uber_file',
      'other_file',
      'spouse_t4_file',
      'spouse_t4a_file',
      'spouse_t5_file',
      'spouse_t5007_file',
      'spouse_t5008_file',
      'spouse_t2202a_file',
      'spouse_uber_file',
      'spouse_other_file',
    ];

    // fields to be stored in local storage
    const formFields = [
      'first_name',
      'middle_name',
      'last_name',
      'email',
      'phone',
      'date_of_birth',
      'street_address',
      'city',
      'province',
      'postal_code',
      'sin_number',
      'income_from_home',
      'date_of_entry',
      'moving_date',
      'benefit_status',
      'province_from',
      'province_to',
      'first_time_form_filling_status',
      'total_rent',
      'moved_from_province',
      'dec_marrige_status',
      'is_spouse_canadian_resident',
      'spouse_first_name',
      'spouse_middle_name',
      'spouse_last_name',
      'spouse_income_from_home',
      'spouse_email',
      'spouse_phone',
      'spouse_dob',
      'spouse_first_time_form_filling_status',
      'spouse_sin_number',
      'spouse_moved_from_province',
      'spouse_date_of_entry',
      'spouse_moving_date',
      'spouse_province_from',
      'spouse_province_to',
      'rent_obj',
      'spouse_canadian_status',
      'first_time_home_buyer',
      'property_sell',
      'property_more_than_1_lakh',
      'rental_income',
      'rental_amount',
      'other_expenses',
      'expenses',
      'bc_hydro_bill',
      'utilities',
      'repairs',
      'bank_interest',
      'property_tax',
      'insurance',
      'internet',
      'fortis_bc',
      'property_owner_name',
      'share_of_property_owner',
      'property_address',
      'canadian_status',
      'child_name',
      'child_dob',
      'donation_amount',
      'medical_expense',
      'child_details',
      'self_employed_income',
      'self_employed_amount',
      'has_gst',
      'gst_no',
      'gas_expense',
      'repairs_expense',
      'insurance_expense',
      'phone_bill_expenses',
      'meal_expenses',
      'car_model_year',
      'car_model',
      'car_purchase_year',
      'car_price_paid',
      'employedOtherExpenses',
      'employed_other_income',
      'spouseEmployedOtherExpenses',
      'spouseWfhEmployedOtherExpenses',
      'employed_income',
      'internet_bill',
      'insurance_bill',
      'equipment_bill',
      'utilities_bill',
      'hydro_bill',
      'rent_bill',
      'wfhEmployedOtherExpenses',
      'work_from_home',
      'spouse_work_from_home',
      'claim_expense',
      'has_agreed',
      'child_care_expense',
      'spouse_self_employed_income',
      'spouse_self_employed_amount',
      'spouse_has_gst',
      'spouse_gst_no',
      'spouse_gas_expense',
      'spouse_repairs_expense',
      'spouse_insurance_expense',
      'spouse_phone_bill_expenses',
      'spouse_meal_expenses',
      'spouse_car_model',
      'spouse_car_model_year',
      'spouse_car_purchase_year',
      'spouse_car_price_paid',
      'spouse_claim_expense',
      'spouse_internet_bill',
      'spouse_insurance_bill',
      'spouse_equipment_bill',
      'spouse_stationery_bill',
      'spouse_utilities_bill',
      'spouse_hydro_bill',
      'spouse_rent_bill',
      'long_haul_truck_driver',
      'form_note',
      'northern_resident_benefit',
      'filed_with_nagra_before',
    ];

    if (
      formFields.includes(name) ||
      updatedData.landlords !== userData.landlords ||
      updatedData.generalOtherExpenses !== userData.generalOtherExpenses ||
      updatedData.dependentChildren !== userData.dependentChildren
    ) {
      // Create a copy of updatedData without file fields
      const dataToStore = { ...updatedData };
      fileFields.forEach((field) => delete dataToStore[field]);
      localStorage.setItem('userFormData', JSON.stringify(dataToStore));
    }
  };

  // Clear Nagra filing status on mount to ensure the gatekeeper shows every time the form is opened
  useEffect(() => {
    setUserData((prev: any) => ({
      ...prev,
      filed_with_nagra_before: '',
    }));
  }, []);

  // VALIDATION: Section validation for Next button
  const validateCurrentSectionForm = () => {
    const { sectionErrors, isValid } = validateCurrentSection(
      activeSection,
      userData,
    );

    setErrors(sectionErrors);
    return isValid;
  };

  // next button functionality
  const handleNext = () => {
    if (!validateCurrentSectionForm()) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill all required fields in the current section',
      });
      return;
    }

    const currentIndex = sectionHeaders.findIndex(
      (section) => section.key === activeSection,
    );
    if (currentIndex < sectionHeaders.length - 1) {
      setActiveSection(sectionHeaders[currentIndex + 1].key);
    }
  };

  const handleFindMyInfo = async () => {
    const sectionErrors: any = {};
    let isError = false;

    if (window.cn(userData.returningSIN)) {
      sectionErrors.returningSIN = "SIN Number is required *";
      isError = true;
    }
    if (window.cn(userData.returningEmail)) {
      sectionErrors.returningEmail = "Email Address is required *";
      isError = true;
    }

    if (isError) {
      setErrors(sectionErrors);
      return;
    }

    setIsSearchingClient(true);
    try {
      const response = await getTwoFilteredDetails(
        GET_EMPLOYEE_FORM_BY_SIN_EMAIL,
        'email',
        userData.returningEmail,
        'sin_number',
        userData.returningSIN,
      );

      if (response) {
        let retrievedData = response.data || response;
        if (Array.isArray(retrievedData)) retrievedData = retrievedData[0];

        if (retrievedData && typeof retrievedData === 'object' && Object.keys(retrievedData).length > 0) {
          const finalData: any = { ...retrievedData };

          // 1. Parse Nested JSON Fields
          const parseJsonField = (field: string, targetKey?: string) => {
            if (typeof finalData[field] === 'string' && finalData[field].trim()) {
              try {
                const parsed = JSON.parse(finalData[field]);
                if (targetKey) finalData[targetKey] = parsed;
                else Object.assign(finalData, parsed);
              } catch (e) {
                console.error(`Error parsing ${field}`, e);
              }
            }
          };

          // Parse root level JSON strings
          parseJsonField('expenses');
          parseJsonField('employed_income');
          parseJsonField('work_from_home_expense');
          parseJsonField('child_details', 'dependentChildren');
          parseJsonField('rent_obj', 'landlords');
          parseJsonField('employed_other_income', 'generalOtherExpenses');
          parseJsonField('other_expenses', 'employedOtherExpenses');
          parseJsonField('work_from_home_other_expense', 'wfhEmployedOtherExpenses');

          // 2. Parse and Map Spouse Details
          if (finalData.spouse_details) {
            try {
              const spouse = typeof finalData.spouse_details === 'string' ? JSON.parse(finalData.spouse_details) : finalData.spouse_details;

              // Map spouse fields to state keys (normalization)
              Object.assign(finalData, {
                ...spouse,
                spouse_sin_number: spouse.sin_number,
                spouse_dob: spouse.spouse_date_of_birth,
              });

              // Handle double-nested spouse expenses
              if (spouse.spouseSelfEmployedExpenses) Object.assign(finalData, JSON.parse(spouse.spouseSelfEmployedExpenses));
              if (spouse.spouseWFHEmployedExpenses) Object.assign(finalData, JSON.parse(spouse.spouseWFHEmployedExpenses));
              if (spouse.spouseSelfEmployedOtherExpenses) finalData.spouseEmployedOtherExpenses = JSON.parse(spouse.spouseSelfEmployedOtherExpenses);
              if (spouse.spouseWFHEmployedOtherExpenses) finalData.spouseWfhEmployedOtherExpenses = JSON.parse(spouse.spouseWFHEmployedOtherExpenses);

            } catch (e) {
              console.error("Error parsing spouse_details", e);
            }
          }

          Object.keys(finalData).forEach(key => {
            const val = finalData[key];
            if (val !== null && typeof val === 'boolean') {
              // Radio buttons expect '1' (yes) or '0' (no), not 'true'/'false'
              finalData[key] = val ? '1' : '0';
            } else if (val !== null && typeof val === 'number') {
              finalData[key] = val.toString();
            }
          });

          setUserData((prev: any) => ({
            ...prev,
            ...finalData,
            filed_with_nagra_before: '0',
          }));

          setActiveSection('personal');

          toast.current?.show({
            severity: 'success',
            summary: 'Information Retrieved',
            detail: 'Your previous filing details, including spouse info, have been successfully loaded.',
          });
          return;
        }
      }

      // If we reach here, it means response was falsy or didn't contain valid data
      toast.current?.show({
        severity: 'error',
        summary: 'Record Not Found',
        detail:
          response?.message ||
          "We couldn't find a filing record with those details. Please check and try again.",
      });
    } catch (error: any) {
      console.error('Error finding info:', error);

      // Handle 404 specifically as a "Record Not Found" case
      if (error.response?.status === 404) {
        toast.current?.show({
          severity: 'error',
          summary: 'Record Not Found',
          detail: "We couldn't find a filing record with those details. Please check and try again.",
        });
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Search Error',
          detail: 'An error occurred while retrieving your information. Please try again later.',
        });
      }
    } finally {
      setIsSearchingClient(false);
    }
  };

  // previous button functionality
  const handlePrevious = () => {
    const currentIndex = sectionHeaders.findIndex(
      (section) => section.key === activeSection,
    );
    if (currentIndex > 0) {
      setActiveSection(sectionHeaders[currentIndex - 1].key);
    } else {
      setActiveSection('identification');
    }
  };

  // Full form validation for Submit button
  const submitUserForm = async (type: any) => {
    try {
      if (type == 'pdf') {
        setDownloadPDFLoading(true);
      } else {
        setSubmitLoading(true);
      }

      // general Expenses obj
      const generalExpenses = {
        bc_hydro_bill: userData.bc_hydro_bill,
        utilities: userData.utilities,
        repairs: userData.repairs,
        bank_interest: userData.bank_interest,
        property_tax: userData.property_tax,
        insurance: userData.insurance,
        internet: userData.internet,
        fortis_bc: userData.fortis_bc,
      };

      // self employed expenses obj
      const selfEmployedExpenses = {
        gas_expense: userData.gas_expense,
        repairs_expense: userData.repairs_expense,
        insurance_expense: userData.insurance_expense,
        phone_bill_expenses: userData.phone_bill_expenses,
        meal_expenses: userData.meal_expenses,
        car_model: userData.car_model,
        car_model_year: userData.car_model_year,
        car_purchase_year: userData.car_purchase_year,
        car_price_paid: userData.car_price_paid,
      };

      // spouse self employed expense obj
      const spouseSelfEmployedExpenses = {
        spouse_self_employed_income: userData.spouse_self_employed_income,
        spouse_self_employed_amount: userData.spouse_self_employed_amount,
        spouse_has_gst: userData.spouse_has_gst,
        spouse_gst_no: userData.spouse_gst_no,
        spouse_gas_expense: userData.spouse_gas_expense,
        spouse_repairs_expense: userData.spouse_repairs_expense,
        spouse_insurance_expense: userData.spouse_insurance_expense,
        spouse_phone_bill_expenses: userData.spouse_phone_bill_expenses,
        spouse_meal_expenses: userData.spouse_meal_expenses,
        spouse_car_model: userData.spouse_car_model,
        spouse_car_model_year: userData.spouse_car_model_year,
        spouse_car_purchase_year: userData.spouse_car_purchase_year,
        spouse_car_price_paid: userData.spouse_car_price_paid,
      };

      // wfh employed obj
      const wfhEmployedExpenses = {
        internet_bill: userData.internet_bill,
        insurance_bill: userData.insurance_bill,
        equipment_bill: userData.equipment_bill,
        stationery_bill: userData.stationery_bill,
        utilities_bill: userData.utilities_bill,
        hydro_bill: userData.hydro_bill,
        rent_bill: userData.rent_bill,
      };

      // spouse work from home expenses
      const spouseWfhEmployedExpenses = {
        spouse_work_from_home: userData.spouse_work_from_home,
        spouse_claim_expense: userData.spouse_claim_expense,
        spouse_internet_bill: userData.spouse_internet_bill,
        spouse_insurance_bill: userData.spouse_insurance_bill,
        spouse_equipment_bill: userData.spouse_equipment_bill,
        spouse_stationery_bill: userData.spouse_stationery_bill,
        spouse_utilities_bill: userData.spouse_utilities_bill,
        spouse_hydro_bill: userData.spouse_hydro_bill,
        spouse_rent_bill: userData.spouse_rent_bill,
      };

      // share of property owner obj
      const ownerOfPropertyWithShare = {
        property_owner_name: `${userData?.first_name} ${userData?.middle_name} ${userData?.last_name}`,
        share_of_property_owner: userData?.share_of_property_owner || '',
      };

      // filter child detail
      const filteredChildDetails = userData?.dependentChildren?.filter(
        (child: any) => child.child_name !== '' || child.child_dob !== null,
      );

      // // filter other expenses if value entered then only append Json
      // const filteredGeneralOtherExpenses = userData?.filteredChildDetails?.filter((item: any) => item.amount !== "" || item.name !== "");
      const filteredExpenses = userData?.generalOtherExpenses?.filter(
        (exp: any) =>
          exp.name !== '' || (exp.amount !== '' && exp.amount !== null),
      );

      // filter self employed other expenses
      const filteredSelfEmployedExpenses =
        userData?.employedOtherExpenses?.filter(
          (exp: any) =>
            exp.name !== '' || (exp.amount !== '' && exp.amount !== null),
        );

      // filter spouse self employed other expenses
      const filteredSpouseSelfEmployedExpenses =
        userData?.spouseEmployedOtherExpenses?.filter(
          (exp: any) =>
            exp.name !== '' || (exp.amount !== '' && exp.amount !== null),
        );

      // tax payer wfh other expenses
      const filteredWfhEmployedExpenses =
        userData?.wfhEmployedOtherExpenses?.filter(
          (exp: any) =>
            exp.name !== '' || (exp.amount !== '' && exp.amount !== null),
        );

      // spouse wfh other expenses
      const filteredSpouseWfhEmployedExpenses =
        userData?.spouseWfhEmployedOtherExpenses?.filter(
          (exp: any) =>
            exp.name !== '' || (exp.amount !== '' && exp.amount !== null),
        );

      const formData: any = new FormData();

      // Text fields
      formData.append('first_name', userData.first_name || '');
      formData.append('middle_name', userData.middle_name || '');
      formData.append('last_name', userData.last_name || '');
      formData.append('street_address', userData.street_address || '');
      formData.append('city', userData.city || '');
      formData.append('province', userData.province || '');
      formData.append('postal_code', userData.postal_code || '');
      formData.append('email', userData.email || '');
      formData.append('phone', userData.phone || '');
      formData.append('sin_number', userData.sin_number || '');
      if (!window.cn(userData?.date_of_birth)) {
        formData.append('date_of_birth', userData.date_of_birth || '');
      }
      formData.append('dec_marrige_status', userData.dec_marrige_status || '');

      if (!window.cn(userData?.marrige_status_changed_date)) {
        formData.append(
          'marrige_status_changed_date',
          userData.marrige_status_changed_date || '',
        );
      }

      // append benefit status only if province is BC || Manitoba

      if (
        userData.province == 'British Columbia' ||
        userData?.province == 'Manitoba'
      ) {
        formData.append('benefit_status', userData.benefit_status || '');
      }
      if (
        userData?.dec_marrige_status === 'Married' ||
        userData?.dec_marrige_status === 'Living common-law'
      ) {
        formData.append(
          'is_spouse_canadian_resident',
          userData.is_spouse_canadian_resident || '',
        );
      }
      formData.append(
        'first_time_form_filling_status',
        userData.first_time_form_filling_status || '',
      );

      if (
        (userData.province == 'British Columbia' ||
          userData?.province == 'Manitoba') &&
        userData?.is_spouse_canadian_resident == '1'
      ) {
        formData.append(
          'spouse_canadian_status',
          userData.spouse_canadian_status || '',
        );
      }

      // general information

      formData.append(
        'first_time_home_buyer',
        userData.first_time_home_buyer || '',
      );
      formData.append('property_sell', userData.property_sell || '');
      formData.append(
        'property_more_than_1_lakh',
        userData.property_more_than_1_lakh || '',
      );
      formData.append('rental_income', userData.rental_income || '');
      formData.append(
        'long_haul_truck_driver',
        userData.long_haul_truck_driver || '',
      );

      if (userData.rental_income == '1') {
        formData.append('rental_amount', userData.rental_amount || '0');
      }

      // status in canada

      formData.append('canadian_status', userData.canadian_status || '');

      if (!window.cn(userData?.child_care_expense)) {
        formData.append("child_care_expense", userData.child_care_expense || "");
      }

      if (!window.cn(userData?.donation_amount) && userData?.donation_amount !== null) {
        formData.append("donation_amount", userData.donation_amount || "");
      }

      if (!window.cn(userData?.medical_expense) && userData?.medical_expense !== null) {
        formData.append("medical_expense", userData.medical_expense || "");
      }

      if (!window.cn(userData.form_note) && userData.form_note !== '-') {
        formData.append('form_note', userData.form_note);
      }
      if (!window.cn(userData.northern_resident_benefit) && userData.northern_resident_benefit !== '-') {
        formData.append('northern_resident_benefit', userData.northern_resident_benefit);
      }

      // Form filling for the FIRST TIME
      if (userData.first_time_form_filling_status === '1') {
        formData.append(
          'income_from_home',
          userData.income_from_home ?? '0.00',
        );

        formData.append('date_of_entry', userData.date_of_entry || '');
      }

      // FORM FILLING FOR FIRST TIME = NO
      if (userData.first_time_form_filling_status === '0') {
        formData.append(
          'moved_from_province',
          userData.moved_from_province || '',
        );
      }

      // if user moved from province == yes
      if (
        userData.first_time_form_filling_status === '0' &&
        userData.moved_from_province === '1'
      ) {
        formData.append('moving_date', userData.moving_date || '');

        formData.append('province_from', userData.province_from || '');

        formData.append('province_to', userData.province_to || '');
      }

      if (userData?.benefit_status === '1') {
        formData.append('rent_obj', JSON.stringify(userData?.landlords || []));
      }

      // append other expense array of obj
      if (userData?.rental_income == '1') {
        formData.append('expenses', JSON.stringify(generalExpenses || {}));
        formData.append('property_address', userData?.property_address || '');
        formData.append(
          'owner_with_percentage',
          JSON.stringify(ownerOfPropertyWithShare || {}),
        );
        // formData.append("other_expenses", JSON.stringify(userData?.generalOtherExpenses || null));
        if (!window.cn(userData?.generalOtherExpenses)) {
          if (filteredSelfEmployedExpenses?.length > 0) {
            formData.append(
              'other_expenses',
              JSON.stringify(filteredSelfEmployedExpenses || {}),
            );
          }
        }
      }

      formData.append(
        'self_employed_income',
        userData.self_employed_income || '',
      );
      formData.append('work_from_home', userData.work_from_home || '');

      // append self employed expenses if self employed
      if (userData?.self_employed_income == '1') {
        formData.append(
          'self_employed_amount',
          userData.self_employed_amount || '',
        );
        formData.append(
          'employed_income',
          JSON.stringify(selfEmployedExpenses || {}),
        );
        formData.append('has_gst', userData?.has_gst || '');
        // formData.append("employed_other_income", JSON.stringify(userData?.employedOtherExpenses || null));
        if (!window.cn(userData?.employedOtherExpenses)) {
          if (filteredExpenses?.length > 0) {
            formData.append(
              'employed_other_income',
              JSON.stringify(filteredExpenses || {}),
            );
          }
        }
      }

      // append wfh employed obj if wfh is yes
      if (userData?.work_from_home == '1') {
        formData.append(
          'work_from_home_expense',
          JSON.stringify(wfhEmployedExpenses || {}),
        );
        formData.append('claim_expense', userData?.claim_expense || '');
        // formData.append("work_from_home_other_expense", JSON.stringify(userData?.wfhEmployedOtherExpenses || null));
        if (!window.cn(userData?.wfhEmployedOtherExpenses)) {
          if (filteredWfhEmployedExpenses?.length > 0) {
            formData.append(
              'work_from_home_other_expense',
              JSON.stringify(filteredWfhEmployedExpenses || {}),
            );
          }
        }
      }

      // append gst no if has_gst
      if (userData?.has_gst == '1') {
        formData.append('gst_no', userData?.gst_no || '');
      }

      // append child details object if value entered
      if (!window.cn(userData.dependentChildren)) {
        if (filteredChildDetails?.length > 0) {
          formData.append(
            'child_details',
            JSON.stringify(filteredChildDetails || {}),
          );
        }
      }

      // if (!window.cn(userData?.dependentChildren)) {
      //   formData.append("child_details", JSON.stringify(userData?.dependentChildren || {}));
      // }

      // append t5008_file
      if (userData.t5008_file instanceof File) {
        formData.append('t5008_file', userData.t5008_file);
      }

      // append t2202a file
      if (userData.t2202a_file instanceof File) {
        formData.append('t2202a_file', userData.t2202a_file);
      }

      // append t4_file single as well as multiple
      if (userData.t4_file) {
        // Single file
        if (userData.t4_file instanceof File) {
          formData.append('t4_file', userData.t4_file);
        }
        // Multiple files
        else if (Array.isArray(userData.t4_file)) {
          for (let i = 0; i < userData.t4_file.length; i++) {
            formData.append('t4_file', userData.t4_file[i]);
          }
        }
      }

      // append t4a_file
      if (userData.t4a_file) {
        // Single file
        if (userData.t4a_file instanceof File) {
          formData.append('t4a_file', userData.t4a_file);
        }
        // Multiple files
        else if (Array.isArray(userData.t4a_file)) {
          for (let i = 0; i < userData.t4a_file.length; i++) {
            formData.append('t4a_file', userData.t4a_file[i]);
          }
        }
      }

      // append uber file
      if (userData.uber_file) {
        // Single file
        if (userData.uber_file instanceof File) {
          formData.append('uber_file', userData.uber_file);
        }
        // Multiple files
        else if (Array.isArray(userData.uber_file)) {
          for (let i = 0; i < userData.uber_file.length; i++) {
            formData.append('uber_file', userData.uber_file[i]);
          }
        }
      }

      // append other_file single as well as multiple
      if (userData.other_file) {
        // Single file
        if (userData.other_file instanceof File) {
          formData.append('other_file', userData.other_file);
        }
        // Multiple files
        else if (Array.isArray(userData.other_file)) {
          for (let i = 0; i < userData.other_file.length; i++) {
            formData.append('other_file', userData.other_file[i]);
          }
        }
      }

      // append t5_file
      if (userData.t5_file instanceof File) {
        formData.append('t5_file', userData.t5_file);
      }

      // append document_sign
      if (userData.document_sign instanceof File) {
        formData.append('document_sign', userData.document_sign);
      }

      // append t5file
      if (userData.t5007_file instanceof File) {
        formData.append('t5007_file', userData.t5007_file);
      }

      // Spouse details including files as keys
      if (
        userData?.dec_marrige_status === 'Married' ||
        userData?.dec_marrige_status === 'Living common-law'
      ) {
        const spouseData: any = {
          spouse_first_name: userData.spouse_first_name || '',
          ...(!window.cn(userData.spouse_middle_name) && userData.spouse_middle_name !== '-' && {
            spouse_middle_name: userData.spouse_middle_name,
          }),
          spouse_last_name: userData.spouse_last_name || '',
          street_address: userData.street_address || '',
          city: userData.city || '',
          province: userData.province || '',
          postal_code: userData.postal_code || '',
          sin_number: userData.spouse_sin_number || '',
          spouse_date_of_birth: userData.spouse_dob || '',
          spouse_first_time_form_filling_status:
            userData?.spouse_first_time_form_filling_status || '',

          ...(userData?.spouse_first_time_form_filling_status !== '1' && {
            spouse_moved_from_province:
              userData?.spouse_moved_from_province || '',
          }),

          // conditionaly add phone and email
          ...(userData.is_spouse_canadian_resident === '1' &&
            userData?.spouse_email && {
            spouse_email: userData.spouse_email,
          }),

          ...(userData.is_spouse_canadian_resident === '1' &&
            userData?.spouse_phone && {
            spouse_phone: userData.spouse_phone,
          }),
        };

        // append spouse self employed expenses if spouse canadaian resident
        if (userData?.is_spouse_canadian_resident == '1') {
          spouseData.spouseSelfEmployedExpenses = JSON.stringify(
            spouseSelfEmployedExpenses,
          );
          spouseData.spouseWFHEmployedExpenses = JSON.stringify(
            spouseWfhEmployedExpenses,
          );
          if (!window.cn(userData?.spouseEmployedOtherExpenses)) {
            if (filteredSpouseSelfEmployedExpenses?.length > 0) {
              spouseData.spouseSelfEmployedOtherExpenses = JSON.stringify(
                filteredSpouseSelfEmployedExpenses,
              );
            }
          }
          // spouse wfh other expenses
          if (!window.cn(userData?.spouseWfhEmployedOtherExpenses)) {
            if (filteredSpouseWfhEmployedExpenses?.length > 0) {
              spouseData.spouseWFHEmployedOtherExpenses = JSON.stringify(
                filteredSpouseWfhEmployedExpenses,
              );
            }
          }
        }

        // if spouse not canadian resident
        if (userData?.is_spouse_canadian_resident === '0') {
          spouseData.spouse_income_from_home =
            userData?.spouse_income_from_home || '';
        }

        //  SPOUSE FILLING FORM FOR FIRST TIME = YES
        if (userData.spouse_first_time_form_filling_status === '1') {
          spouseData.date_of_entry = userData.spouse_date_of_entry || '';
          spouseData.spouse_income_from_home =
            userData.spouse_income_from_home ?? '0.00';
        }

        //  SPOUSE FILLING FORM FOR FIRST TIME = NO & SPOUSE MOVED FROM PROVINCE = YES =====
        if (
          userData.spouse_first_time_form_filling_status === '0' &&
          userData.spouse_moved_from_province === '1'
        ) {
          spouseData.moving_date = userData.spouse_moving_date || '';
          spouseData.province_from = userData.spouse_province_from || '';
          spouseData.province_to = userData.spouse_province_to || '';
        }

        formData.append('spouse_details', JSON.stringify(spouseData));

        // Append actual spouse files
        if (userData.spouse_t5008_file instanceof File) {
          formData.append('spouse_t5008_file', userData.spouse_t5008_file);
        }

        // append spouse t2202a file
        if (userData.spouse_t2202a_file instanceof File) {
          formData.append('spouse_t2202a_file', userData.spouse_t2202a_file);
        }

        // append spouse t4 file
        if (userData.spouse_t4_file && Array.isArray(userData.spouse_t4_file)) {
          userData.spouse_t4_file.forEach((file: File) =>
            formData.append('spouse_t4_file', file),
          );
        } else if (userData.spouse_t4_file instanceof File) {
          formData.append('spouse_t4_file', userData.spouse_t4_file);
        }

        // append spouse t4a file
        if (
          userData.spouse_t4a_file &&
          Array.isArray(userData.spouse_t4a_file)
        ) {
          userData.spouse_t4a_file.forEach((file: File) =>
            formData.append('spouse_t4a_file', file),
          );
        } else if (userData.spouse_t4a_file instanceof File) {
          formData.append('spouse_t4a_file', userData.spouse_t4a_file);
        }

        // append spouse uber file
        if (
          userData.spouse_uber_file &&
          Array.isArray(userData.spouse_uber_file)
        ) {
          userData.spouse_uber_file.forEach((file: File) =>
            formData.append('spouse_uber_file', file),
          );
        } else if (userData.spouse_uber_file instanceof File) {
          formData.append('spouse_uber_file', userData.spouse_uber_file);
        }

        // append spouse_other_file file
        if (
          userData.spouse_other_file &&
          Array.isArray(userData.spouse_other_file)
        ) {
          userData.spouse_other_file.forEach((file: File) =>
            formData.append('spouse_other_file', file),
          );
        } else if (userData.spouse_other_file instanceof File) {
          formData.append('spouse_other_file', userData.spouse_other_file);
        }

        // append spouse_t5007_file file of spose
        if (userData.spouse_t5007_file instanceof File) {
          formData.append('spouse_t5007_file', userData.spouse_t5007_file);
        }

        // append spouse_t5_file file of spose
        if (userData.spouse_t5_file instanceof File) {
          formData.append('spouse_t5_file', userData.spouse_t5_file);
        }
      }
      if (type == 'pdf') {
        formData.append('type', type);
      }

      // api call
      const response = await postAllData(CONNECT_WITH_US_FORM, formData);

      if (response && response?.data) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response?.message,
        });
        // Reset form after successful submission
        // localStorage.removeItem("userFormData");
        // setUserData({});
        setActiveSection('personal');
        setSubmitLoading(false);
        setDownloadPDFLoading(false);
        setTaxPayerId(response?.data);
        setBasicTaxSummary(response);
        const paymentUrl = response?.data;
        if (type == 'pdf') {
          window.open(paymentUrl, '_blank');
        } else {
          window.location.href = paymentUrl;
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Message',
        detail: error?.response?.data?.message,
      });
      setSubmitLoading(false);
      setDownloadPDFLoading(false);
    }
  };

  // Handle dependent child change on input
  const handleDependentChildChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const dependentChildren = [...(prev.dependentChildren ?? [])];
      dependentChildren[index] = {
        ...dependentChildren[index],
        [field]:
          field === 'child_dob' && value
            ? moment(value).format('YYYY-MM-DD')
            : value,
      };
      return { ...prev, dependentChildren };
    });

    setErrors((prev: any) => {
      const childErrors = prev.dependentChildren
        ? [...prev.dependentChildren]
        : [];
      if (childErrors[index]?.[field]) childErrors[index][field] = '';
      return { ...prev, dependentChildren: childErrors };
    });
  };

  // Handle removing dependent child
  const handleRemoveDependentChild = (index: number) => {
    const newDependentChildren = userData?.dependentChildren?.filter(
      (_: any, i: number) => i !== index,
    );
    onInputChange({ value: newDependentChildren }, 'dependentChildren');
  };

  // Handle adding a new dependent child
  const handleAddDependentChild = () => {
    setUserData((prev: any) => {
      const newDependentChildren = [
        ...(prev?.dependentChildren || []),
        { child_name: '', child_dob: null },
      ];
      onInputChange({ value: newDependentChildren }, 'dependentChildren');
      return { ...prev, dependentChildren: newDependentChildren };
    });
  };

  // Handle other expense change on input
  const handleOtherExpenseChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const generalOtherExpenses = [...(prev.generalOtherExpenses ?? [])];
      generalOtherExpenses[index] = {
        ...generalOtherExpenses[index],
        [field]: value,
      };
      return { ...prev, generalOtherExpenses };
    });

    setErrors((prev: any) => {
      const otherExpenseErrors = prev.generalOtherExpenses
        ? [...prev.generalOtherExpenses]
        : [];
      if (otherExpenseErrors[index]?.[field])
        otherExpenseErrors[index][field] = '';
      return { ...prev, generalOtherExpenses: otherExpenseErrors };
    });
  };

  // Handle removing other expense
  const handleRemoveOtherExpenses = (index: number) => {
    const newOtherExpenses = userData?.generalOtherExpenses?.filter(
      (_: any, i: number) => i !== index,
    );
    onInputChange({ value: newOtherExpenses }, 'generalOtherExpenses');
  };

  // Handle adding a new other expenses
  const handleAddOtherExpenses = () => {
    setUserData((prev: any) => {
      const newOtherExpenses = [
        ...(prev?.generalOtherExpenses || []),
        { name: '', amount: '' },
      ];
      onInputChange({ value: newOtherExpenses }, 'generalOtherExpenses');
      return { ...prev, generalOtherExpenses: newOtherExpenses };
    });
  };

  // Handle change in individual landlord name input
  const handleLandlordChange = (field: string, value: any, index: number) => {
    setUserData((prev: any) => {
      const landlords = [...(prev.landlords ?? [])];
      landlords[index] = { ...landlords[index], [field]: value };
      return { ...prev, landlords };
    });

    setErrors((prev: any) => {
      const landlordErrors = prev.landlords ? [...prev.landlords] : [];
      if (landlordErrors[index]?.[field]) landlordErrors[index][field] = '';
      return { ...prev, landlords: landlordErrors };
    });
  };

  // Handle removing a landlord name
  const handleRemoveLandlord = (index: number) => {
    const newLandlords = userData?.landlords?.filter(
      (_: any, i: number) => i !== index,
    );
    onInputChange({ value: newLandlords }, 'landlords');
  };

  // Handle adding a new landlord name
  const handleAddLandlord = () => {
    setUserData((prev: any) => {
      const newLandlords = [
        ...(prev?.landlords || []),
        { name: '', amount: 0, months: '' },
      ];
      onInputChange({ value: newLandlords }, 'landlords');
      return { ...prev, landlords: newLandlords };
    });
  };

  // ..................................................... self employed other expenses ..............................

  // Handle Employed other expense change on input
  const handleEmployedOtherExpenseChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const employedOtherExpenses = [...(prev.employedOtherExpenses ?? [])];
      employedOtherExpenses[index] = {
        ...employedOtherExpenses[index],
        [field]: value,
      };
      return { ...prev, employedOtherExpenses };
    });

    setErrors((prev: any) => {
      const otherEmployedExpenseError = prev.employedOtherExpenses
        ? [...prev.employedOtherExpenses]
        : [];
      if (otherEmployedExpenseError[index]?.[field])
        otherEmployedExpenseError[index][field] = '';
      return { ...prev, employedOtherExpenses: otherEmployedExpenseError };
    });
  };

  // Handle removing Employed other expense
  const handleRemoveEmployedOtherExpenses = (index: number) => {
    const newOtherEmployedExpense = userData?.employedOtherExpenses?.filter(
      (_: any, i: number) => i !== index,
    );
    onInputChange({ value: newOtherEmployedExpense }, 'employedOtherExpenses');
  };

  // Handle adding a new Employed other expenses
  const handleAddEmployedOtherExpenses = () => {
    setUserData((prev: any) => {
      const newOtherEmployedExpense = [
        ...(prev?.employedOtherExpenses || []),
        { name: '', amount: '' },
      ];
      onInputChange(
        { value: newOtherEmployedExpense },
        'employedOtherExpenses',
      );
      return { ...prev, employedOtherExpenses: newOtherEmployedExpense };
    });
  };

  // ............................................... spouse self employed other expenses .............................

  // Handle spouse Employed other expense change on input
  const handleSpouseEmployedOtherExpenseChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const spouseEmployedOtherExpenses = [
        ...(prev.spouseEmployedOtherExpenses ?? []),
      ];
      spouseEmployedOtherExpenses[index] = {
        ...spouseEmployedOtherExpenses[index],
        [field]: value,
      };
      return { ...prev, spouseEmployedOtherExpenses };
    });

    setErrors((prev: any) => {
      const otherSpouseEmployedExpenseError = prev.spouseEmployedOtherExpenses
        ? [...prev.spouseEmployedOtherExpenses]
        : [];
      if (otherSpouseEmployedExpenseError[index]?.[field])
        otherSpouseEmployedExpenseError[index][field] = '';
      return {
        ...prev,
        spouseEmployedOtherExpenses: otherSpouseEmployedExpenseError,
      };
    });
  };

  // Handle removing spouse Employed other expense
  const handleRemoveSpouseEmployedOtherExpenses = (index: number) => {
    const newSpouseOtherEmployedExpense =
      userData?.spouseEmployedOtherExpenses?.filter(
        (_: any, i: number) => i !== index,
      );
    onInputChange(
      { value: newSpouseOtherEmployedExpense },
      'spouseEmployedOtherExpenses',
    );
  };

  // Handle adding a new spouse Employed other expenses
  const handleAddSpouseEmployedOtherExpenses = () => {
    setUserData((prev: any) => {
      const newSpouseOtherEmployedExpense = [
        ...(prev?.spouseEmployedOtherExpenses || []),
        { name: '', amount: '' },
      ];
      onInputChange(
        { value: newSpouseOtherEmployedExpense },
        'spouseEmployedOtherExpenses',
      );
      return {
        ...prev,
        spouseEmployedOtherExpenses: newSpouseOtherEmployedExpense,
      };
    });
  };

  // .................................... WFH Employed add more functionality .................
  // Handle Employed other expense change on input
  const handleWFHEmployedOtherExpenseChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const wfhEmployedOtherExpenses = [
        ...(prev.wfhEmployedOtherExpenses ?? []),
      ];
      wfhEmployedOtherExpenses[index] = {
        ...wfhEmployedOtherExpenses[index],
        [field]: value,
      };
      return { ...prev, wfhEmployedOtherExpenses };
    });

    setErrors((prev: any) => {
      const otherWfhEmployedExpensesError = prev.wfhEmployedOtherExpenses
        ? [...prev.wfhEmployedOtherExpenses]
        : [];
      if (otherWfhEmployedExpensesError[index]?.[field])
        otherWfhEmployedExpensesError[index][field] = '';
      return {
        ...prev,
        wfhEmployedOtherExpenses: otherWfhEmployedExpensesError,
      };
    });
  };

  // Handle removing Employed other expense
  const handleRemoveWFHEmployedOtherExpenses = (index: number) => {
    const newOtherEmployedExpense = userData?.wfhEmployedOtherExpenses?.filter(
      (_: any, i: number) => i !== index,
    );
    onInputChange(
      { value: newOtherEmployedExpense },
      'wfhEmployedOtherExpenses',
    );
  };

  // Handle adding a new Employed other expenses
  const handleAddWFHEmployedOtherExpenses = () => {
    setUserData((prev: any) => {
      const newOtherEmployedExpense = [
        ...(prev?.wfhEmployedOtherExpenses || []),
        { name: '', amount: '' },
      ];
      onInputChange(
        { value: newOtherEmployedExpense },
        'wfhEmployedOtherExpenses',
      );
      return { ...prev, wfhEmployedOtherExpenses: newOtherEmployedExpense };
    });
  };

  // ....................................... spouse wfh other expenses ............................
  // Handle spouse wfh Employed other expense change on input
  const handleSpouseWFHEmployedOtherExpenseChange = (
    field: string,
    value: any,
    index: number,
  ) => {
    setUserData((prev: any) => {
      const spouseWfhEmployedOtherExpenses = [
        ...(prev.spouseWfhEmployedOtherExpenses ?? []),
      ];
      spouseWfhEmployedOtherExpenses[index] = {
        ...spouseWfhEmployedOtherExpenses[index],
        [field]: value,
      };
      return { ...prev, spouseWfhEmployedOtherExpenses };
    });

    setErrors((prev: any) => {
      const otherSpouseWfhEmployedExpensesError =
        prev.spouseWfhEmployedOtherExpenses
          ? [...prev.spouseWfhEmployedOtherExpenses]
          : [];
      if (otherSpouseWfhEmployedExpensesError[index]?.[field])
        otherSpouseWfhEmployedExpensesError[index][field] = '';
      return {
        ...prev,
        spouseWfhEmployedOtherExpenses: otherSpouseWfhEmployedExpensesError,
      };
    });
  };

  // Handle removing spouse wfh Employed other expense
  const handleRemoveSpousespouseWfhEmployedOtherExpenses = (index: number) => {
    const newOtherSpouseEmployedExpense =
      userData?.spouseWfhEmployedOtherExpenses?.filter(
        (_: any, i: number) => i !== index,
      );
    onInputChange(
      { value: newOtherSpouseEmployedExpense },
      'spouseWfhEmployedOtherExpenses',
    );
  };

  // Handle adding a new spouse wfh Employed other expenses
  const handleAddSpousespouseWfhEmployedOtherExpenses = () => {
    setUserData((prev: any) => {
      const newOtherSpouseEmployedExpense = [
        ...(prev?.spouseWfhEmployedOtherExpenses || []),
        { name: '', amount: '' },
      ];
      onInputChange(
        { value: newOtherSpouseEmployedExpense },
        'spouseWfhEmployedOtherExpenses',
      );
      return {
        ...prev,
        spouseWfhEmployedOtherExpenses: newOtherSpouseEmployedExpense,
      };
    });
  };

  // cancel button
  const cancelForm = () => {
    localStorage.removeItem('userFormData');
    setActiveSection('personal');
    setUserData(initialUserData);
    setActiveSection('identification');
    setErrors({});
    setSubmitLoading(false);
  };

  // variable to store first section and last section of form
  const isLastSection =
    activeSection === sectionHeaders[sectionHeaders.length - 1].key;
  const isFirstSection = activeSection === sectionHeaders[0].key;
  const isIdentificationSection = activeSection === 'identification';

  return (
    <>
      <Toast ref={toast} />
      <div className="user-form-container">
        {formSubmitted === false && !isIdentificationSection && (
          <div className="form-header">
            <div className="header-content">
              <h1 className="text-black-alpha-90">NAGRA ACCOUNTING</h1>
            </div>
          </div>
        )}

        {formSubmitted === false ? (
          <Card
            className={`main-form-card tax-flow-card shadow-4 ${isIdentificationSection ? 'tax-flow-card--standalone' : ''
              }`}
          >
            <div
              className={`tax-flow-shell ${isIdentificationSection ? 'tax-flow-shell--standalone' : ''
                }`}
            >
              {!isIdentificationSection && (
                <aside className="tax-flow-sidebar">
                  <FormHeader
                    sectionHeaders={sectionHeaders}
                    activeSection={activeSection}
                    setActiveSection={setActiveSection}
                    validateCurrentSectionForm={validateCurrentSectionForm}
                    toast={toast}
                  />
                </aside>
              )}
              <section className="tax-flow-workspace">
                <div className="form-content tax-flow-content">
                  {/* Identification Section */}
                  {activeSection === 'identification' && (
                    <div className="identification-section tax-intake">
                      <div className="tax-intake-hero">
                        <span className="tax-intake-kicker">
                          Tax filing intake
                        </span>
                        <h2>Welcome to Nagra Accounting</h2>
                        <p>
                          Choose how you want to begin. Returning clients can
                          load saved details, and new clients can jump straight
                          into the form.
                        </p>

                        <div className="tax-intake-steps" aria-hidden="true">
                          <div className="tax-intake-step is-active">
                            <span>1</span>
                            <strong>Identify</strong>
                          </div>
                          <div className="tax-intake-step">
                            <span>2</span>
                            <strong>Complete</strong>
                          </div>
                          <div className="tax-intake-step">
                            <span>3</span>
                            <strong>Review</strong>
                          </div>
                        </div>
                      </div>

                      <div className="tax-intake-panel">
                        <div className="tax-intake-panel-header">
                          <div>
                            <span className="tax-intake-eyebrow">
                              Start here
                            </span>
                            <h3>Have you filed with us before?</h3>
                          </div>
                          <i className="pi pi-check-circle"></i>
                        </div>

                        <div className="tax-choice-grid">
                          <button
                            type="button"
                            className={`tax-choice-card ${userData.filed_with_nagra_before === '1'
                              ? 'is-selected'
                              : ''
                              }`}
                            onClick={() =>
                              onInputChange(
                                { target: { value: '1' } },
                                'filed_with_nagra_before',
                              )
                            }
                          >
                            <span className="tax-choice-icon">
                              <i className="pi pi-history"></i>
                            </span>
                            <span className="tax-choice-copy">
                              <strong>Yes, I am returning</strong>
                              <small>
                                Search your previous filing and continue faster.
                              </small>
                            </span>
                            <i className="pi pi-arrow-right tax-choice-arrow"></i>
                          </button>

                          <button
                            type="button"
                            className={`tax-choice-card ${userData.filed_with_nagra_before === '0'
                              ? 'is-selected'
                              : ''
                              }`}
                            onClick={() => {
                              onInputChange(
                                { target: { value: '0' } },
                                'filed_with_nagra_before',
                              );
                              setActiveSection('personal');
                            }}
                          >
                            <span className="tax-choice-icon">
                              <i className="pi pi-user-plus"></i>
                            </span>
                            <span className="tax-choice-copy">
                              <strong>No, I am new here</strong>
                              <small>
                                Begin a fresh tax filing with guided sections.
                              </small>
                            </span>
                            <i className="pi pi-arrow-right tax-choice-arrow"></i>
                          </button>
                        </div>

                        {errors.filed_with_nagra_before && (
                          <small className="p-error tax-intake-error">
                            {errors.filed_with_nagra_before}
                          </small>
                        )}

                        {userData.filed_with_nagra_before === '1' && (
                          <div className="search-container tax-returning-search">
                            <div className="tax-search-heading">
                              <span>
                                <i className="pi pi-search"></i>
                              </span>
                              <div>
                                <h3>Find your information</h3>
                                <p>
                                  Enter the same SIN and email used on your last
                                  filing.
                                </p>
                              </div>
                            </div>

                            <div className="tax-search-grid">
                              <div className="tax-search-field" style={{ position: 'relative' }}>
                                <label htmlFor="search_sin">
                                  SIN Number <span>*</span>
                                </label>
                                <div className="p-input-icon-left w-full">
                                  <i className="pi pi-id-card"></i>
                                  <InputText
                                    id="search_sin"
                                    value={userData.returningSIN}
                                    onChange={(e) =>
                                      onInputChange(
                                        {
                                          target: {
                                            value: e.target.value.replace(
                                              /[\s-]/g,
                                              '',
                                            ),
                                          },
                                        },
                                        'returningSIN',
                                      )
                                    }
                                    placeholder="123 456 789"
                                    keyfilter="pint"
                                    maxLength={9}
                                    className={`w-full ${errors.returningSIN ? 'p-invalid' : ''
                                      }`}
                                  />
                                </div>
                                {errors.returningSIN && (
                                  <small className="p-error" style={{ position: 'absolute', bottom: '-20px', left: '0', whiteSpace: 'nowrap' }}>
                                    {errors.returningSIN}
                                  </small>
                                )}
                              </div>

                              <div className="tax-search-field" style={{ position: 'relative' }}>
                                <label htmlFor="search_email">
                                  Email Address <span>*</span>
                                </label>
                                <div className="p-input-icon-left w-full">
                                  <i className="pi pi-envelope"></i>
                                  <InputText
                                    id="search_email"
                                    value={userData.returningEmail}
                                    onChange={(e) =>
                                      onInputChange(e, 'returningEmail')
                                    }
                                    placeholder="yourname@example.com"
                                    className={`w-full ${errors.returningEmail ? 'p-invalid' : ''
                                      }`}
                                  />
                                </div>
                                {errors.returningEmail && (
                                  <small className="p-error" style={{ position: 'absolute', bottom: '-20px', left: '0', whiteSpace: 'nowrap' }}>
                                    {errors.returningEmail}
                                  </small>
                                )}
                              </div>

                              <Button
                                label="Search"
                                icon="pi pi-search"
                                className="tax-search-button"
                                onClick={handleFindMyInfo}
                                loading={isSearchingClient}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Personal Information Section */}
                  {activeSection === 'personal' && (
                    <PersonalSection
                      userData={userData}
                      errors={errors}
                      onInputChange={onInputChange}
                      maxDate={maxDate}
                      todayAsMaxDate={todayAsMaxDate}
                      isMarried={isMarried}
                    />
                  )}

                  {/* Employment & Income Section */}
                  {activeSection === 'employment' && (
                    <EmploymentSection
                      userData={userData}
                      errors={errors}
                      onInputChange={onInputChange}
                      provinces={provinces}
                      handleLandlordChange={handleLandlordChange}
                      handleRemoveLandlord={handleRemoveLandlord}
                      handleAddLandlord={handleAddLandlord}
                      handleDependentChildChange={handleDependentChildChange}
                      handleRemoveDependentChild={handleRemoveDependentChild}
                      handleAddDependentChild={handleAddDependentChild}
                      isMarried={isMarried}
                    />
                  )}

                  {/* General Information */}
                  {activeSection === 'general_questions' && (
                    <GeneralInformationSection
                      userData={userData}
                      errors={errors}
                      onInputChange={onInputChange}
                      handleOtherExpenseChange={handleOtherExpenseChange}
                      handleRemoveOtherExpenses={handleRemoveOtherExpenses}
                      handleAddOtherExpenses={handleAddOtherExpenses}
                      handleDependentChildChange={handleDependentChildChange}
                      handleRemoveDependentChild={handleRemoveDependentChild}
                      handleAddDependentChild={handleAddDependentChild}
                      handleEmployedOtherExpenseChange={
                        handleEmployedOtherExpenseChange
                      }
                      handleRemoveEmployedOtherExpenses={
                        handleRemoveEmployedOtherExpenses
                      }
                      handleAddEmployedOtherExpenses={
                        handleAddEmployedOtherExpenses
                      }
                      handleWFHEmployedOtherExpenseChange={
                        handleWFHEmployedOtherExpenseChange
                      }
                      handleRemoveWFHEmployedOtherExpenses={
                        handleRemoveWFHEmployedOtherExpenses
                      }
                      handleAddWFHEmployedOtherExpenses={
                        handleAddWFHEmployedOtherExpenses
                      }
                      handleSpouseEmployedOtherExpenseChange={
                        handleSpouseEmployedOtherExpenseChange
                      }
                      handleRemoveSpouseEmployedOtherExpenses={
                        handleRemoveSpouseEmployedOtherExpenses
                      }
                      handleAddSpouseEmployedOtherExpenses={
                        handleAddSpouseEmployedOtherExpenses
                      }
                      handleSpouseWFHEmployedOtherExpenseChange={
                        handleSpouseWFHEmployedOtherExpenseChange
                      }
                      handleRemoveSpousespouseWfhEmployedOtherExpenses={
                        handleRemoveSpousespouseWfhEmployedOtherExpenses
                      }
                      handleAddSpousespouseWfhEmployedOtherExpenses={
                        handleAddSpousespouseWfhEmployedOtherExpenses
                      }
                      maxDate={maxDate}
                      todayAsMaxDate={todayAsMaxDate}
                      isMarried={isMarried}
                    />
                  )}

                  {/* Documents Section */}
                  {activeSection === 'documents' && (
                    <DocumentsSection
                      userData={userData}
                      errors={errors}
                      onInputChange={onInputChange}
                    />
                  )}

                  {/* Marital Status Section */}
                  {activeSection === 'marital_document' && (
                    <SpouseDocumentsSection
                      userData={userData}
                      errors={errors}
                      onInputChange={onInputChange}
                      isMarried={isMarried}
                    />
                  )}

                  {/* Form Actions */}
                  {activeSection !== 'identification' && (
                    <FormActions
                      isFirstSection={false}
                      isLastSection={isLastSection}
                      handlePrevious={handlePrevious}
                      handleNext={handleNext}
                      submitLoading={submitLoading}
                      cancelForm={cancelForm}
                      formSubmitted={formSubmitted}
                      updateFormState={updateFormState}
                    />
                  )}
                </div>
              </section>
            </div>
          </Card>
        ) : (
          <TaxSummary
            taxPayerId={taxPayerId}
            updatePreviousformState={updatePreviousformState}
            submitUserForm={submitUserForm}
            submitLoading={submitLoading}
            downloadLoading={downloadPDFLoading}
            userData={userData}
            errors={errors}
            onInputChange={onInputChange}
          />
        )}
      </div>
    </>
  );
};

export default UserFormNew;
